// For use with Alpine 3.13.x
(function () {
  const cartStore = {
    cart: window.cart,

    get totalPrice() {
      return this.cart?.total_price;
    },

    get itemCount() {
      return this.cart?.item_count;
    },

    get items() {
      return this.cart?.items;
    },
  };

  const productInterestStore = {
    async init() {
      if (window.customerId === undefined) {
        return;
      }

      let resp = await fetch(`https://cozy-earth-api.fly.dev/v1/customers/${window.customerId}/product-interests`);
      let json = await resp.json();
      this.productInterests = await this.withAvailableProducts(json);
      this.isLoaded = true;
    },

    productInterests: [],
    isLoaded: false,

    // productId -> handle, or null when the product isn't on the storefront (deleted/unpublished)
    handles: {},

    // Drops interests whose product no longer resolves on the storefront, so cards don't sit in
    // their loading state forever. Looks up all unknown ids in one batched query.
    async withAvailableProducts(interests) {
      const unknownIds = [...new Set(interests.map((p) => p.productId))].filter((id) => !(id in this.handles));

      if (unknownIds.length > 0) {
        try {
          let resp = await fetch("https://cozyearth2.myshopify.com/api/2024-10/graphql.json", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token": "0ac50b4d55d0892d3774e8b95543d971",
            },
            body: JSON.stringify({
              operationName: "getProductHandlesByIds",
              query: "query getProductHandlesByIds($ids: [ID!]!) { nodes(ids: $ids) { ... on Product { handle } } }",
              variables: {
                ids: unknownIds.map((id) => `gid://shopify/Product/${id}`),
              },
            }),
          });

          let nodes = resp.ok ? (await resp.json())?.data?.nodes : null;
          if (!Array.isArray(nodes)) {
            return interests;
          }

          unknownIds.forEach((id, i) => {
            this.handles[id] = nodes[i]?.handle ?? null;
          });
        } catch (e) {
          return interests;
        }
      }

      return interests.filter((p) => this.handles[p.productId]);
    },

    async productHandle(productId) {
      if (this.handles[productId]) {
        return this.handles[productId];
      }

      try {
        let resp = await fetch("https://cozyearth2.myshopify.com/api/2024-10/graphql.json", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": "0ac50b4d55d0892d3774e8b95543d971",
          },
          body: JSON.stringify({
            operationName: "getProductHandleById",
            query: "query getProductHandleById($id: ID!) { product(id: $id) { handle } }",
            variables: {
              id: `gid://shopify/Product/${productId}`,
            },
          }),
        });

        if (!resp.ok) {
          return null;
        }

        let respJson = await resp.json();
        return respJson?.data?.product?.handle;
      } catch (e) {
        return null;
      }
    },

    async refreshInterests() {
      let resp = await fetch(`https://cozy-earth-api.fly.dev/v1/customers/${window.customerId}/product-interests`);
      let json = await resp.json();
      this.productInterests = await this.withAvailableProducts(json);
    },

    getInterestId(productId, interest) {
      return this.productInterests.find((p) => p.productId === productId && p.interest === interest)?.interestId;
    },

    async hasInterest(productId, interest) {
      if (!this.isLoaded) {
        await this.init();
      }
      return !!this.productInterests.find((p) => p.productId === productId && p.interest === interest);
    },

    async toggleInterest(productId, interest) {
      const interestId = this.getInterestId(productId, interest);

      if (await this.hasInterest(productId, interest)) {
        await this.deleteInterest(interestId);
      } else {
        await this.createInterest(productId, interest);
      }
    },

    async createInterest(productId, interest) {
      if (window.customerId === undefined) {
        return;
      }

      await fetch(`https://cozy-earth-api.fly.dev/v1/customers/${window.customerId}/product-interests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productId,
          interest,
        }),
      });
      await this.refreshInterests();
    },

    async deleteInterest(interestId) {
      if (window.customerId === undefined) {
        return;
      }

      await fetch(`https://cozy-earth-api.fly.dev/v1/customers/${window.customerId}/product-interests/${interestId}`, {
        method: "DELETE",
      });
      await this.refreshInterests();
    },
  };
  
  const API_BASE = 'https://cozy-earth-api.fly.dev/v1';

  const savedForLaterStore = {
    items: [],
    _isLoggedIn: false,

    async init() {
      this._isLoggedIn = window.customerId !== undefined;

      if (this._isLoggedIn) {
        // Logged in: fetch from API
        try {
          const res = await fetch(`${API_BASE}/customers/${window.customerId}/saved-for-later`);
          if (res.ok) {
            const data = await res.json();
            this.items = data;
            // Migrate any localStorage items to API, then clear localStorage
            this._migrateLocalStorage();
          }
        } catch (err) {
          console.error('Failed to fetch saved-for-later from API:', err);
          // Fall back to localStorage if API fails
          this._loadLocalStorage();
        }
      } else {
        // Guest: use localStorage
        this._loadLocalStorage();
      }
    },

    _loadLocalStorage() {
      try {
        this.items = JSON.parse(localStorage.getItem('saved_for_later') || '[]');
      } catch { this.items = []; }
    },

    _persistLocalStorage() {
      localStorage.setItem('saved_for_later', JSON.stringify(this.items));
    },

    async _migrateLocalStorage() {
      try {
        const local = JSON.parse(localStorage.getItem('saved_for_later') || '[]');
        if (!local.length) return;

        for (const item of local) {
          await this._apiAdd(item);
        }

        localStorage.removeItem('saved_for_later');

        // Re-fetch to get the merged list
        const res = await fetch(`${API_BASE}/customers/${window.customerId}/saved-for-later`);
        if (res.ok) {
          this.items = await res.json();
        }
      } catch (err) {
        console.error('Failed to migrate localStorage to API:', err);
      }
    },

    async _apiAdd(item) {
      return fetch(`${API_BASE}/customers/${window.customerId}/saved-for-later`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variantId: item.variantId,
          productId: item.productId,
          sellingPlanId: item.sellingPlanId || null,
          quantity: item.quantity || 1,
          productTitle: item.productTitle || '',
          variantTitle: item.variantTitle || '',
          variantUrl: item.variantUrl || '',
          imageUrl: item.imageUrl || '',
          sellingPlanName: item.sellingPlanName || '',
          price: item.price || 0,
          options: item.options || [],
        }),
      });
    },

    /** Unique key for an item: variantId + sellingPlanId */
    _key(item) {
      return item.variantId + '_' + (item.sellingPlanId || '');
    },

    async addItem(item) {
      // Update local state immediately
      const key = this._key(item);
      const idx = this.items.findIndex((i) => this._key(i) === key);
      if (idx !== -1) {
        this.items.splice(idx, 1);
      }
      this.items.push({ ...item, savedAt: Date.now() });

      if (this._isLoggedIn) {
        try {
          await this._apiAdd(item);
          // Re-fetch so local items get the server-assigned UUID id
          const res = await fetch(`${API_BASE}/customers/${window.customerId}/saved-for-later`);
          if (res.ok) this.items = await res.json();
        } catch (err) {
          console.error('Failed to save item to API:', err);
        }
      } else {
        this._persistLocalStorage();
      }
    },

    async removeItem(variantId, sellingPlanId) {
      const key = variantId + '_' + (sellingPlanId || '');

      if (this._isLoggedIn) {
        // Find the item's API id before removing from local state
        const item = this.items.find((i) => this._key(i) === key);
        this.items = this.items.filter((i) => this._key(i) !== key);

        if (item?.id) {
          try {
            await fetch(`${API_BASE}/customers/${window.customerId}/saved-for-later/${item.id}`, {
              method: 'DELETE',
            });
          } catch (err) {
            console.error('Failed to delete item from API:', err);
          }
        }
      } else {
        this.items = this.items.filter((i) => this._key(i) !== key);
        this._persistLocalStorage();
      }
    },

    async updateQuantity(variantId, sellingPlanId, qty) {
      const key = variantId + '_' + (sellingPlanId || '');
      const item = this.items.find((i) => this._key(i) === key);
      if (!item) return;

      item.quantity = Math.max(1, qty);

      if (this._isLoggedIn && item.id) {
        try {
          await fetch(`${API_BASE}/customers/${window.customerId}/saved-for-later/${item.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ quantity: item.quantity }),
          });
        } catch (err) {
          console.error('Failed to update quantity in API:', err);
        }
      } else {
        this._persistLocalStorage();
      }
    },

    get count() {
      return this.items.length;
    },
  };

  const bedRotStore = {
    already_voted: false,
  }
  function registerAlpineStores() {
    Alpine.store("cart", cartStore);
    Alpine.store("productInterests", productInterestStore);
     Alpine.store('savedForLater', savedForLaterStore);
     Alpine.store('bedrot', bedRotStore);
  }

  document.addEventListener("alpine:init", registerAlpineStores, { once: true });
})();
